import { classifyStatus, PlaybackError } from './errors';
import { fetchWithCredential, joinUrl, parseRetryAfterMs } from './http';
import { type CredentialSource, type StreamDescriptor } from './types';
import { pollToLive, type WaitForLive } from './waitForLive';

export interface ResolveOptions {
  /**
   * Base URL of the havik-streams API — `https://feed.oddin-video.gg`
   * (production) or `https://feed-dev.oddin-video.gg` (integration). Prefer
   * resolving it from the endpoint directory at runtime over hard-coding it;
   * see Service URL discovery in the API reference.
   */
  baseUrl: string;
  matchUrn: string;
  credential: CredentialSource;
  /**
   * When set, resolve retries internally on 425 TOO_EARLY honoring Retry-After
   * and resolves the instant the stream is live. See WaitForLive.
   */
  waitForLive?: WaitForLive;
  signal?: AbortSignal;
  /**
   * Extra headers attached to every request this call makes to {@link baseUrl}
   * — and ONLY there: never to CDN manifest/segment hosts or DRM license
   * endpoints. For integrations whose API front door wants its own auth
   * signal on top of the publishable key (gateways, operator consoles).
   * `x-api-key` cannot be overridden.
   */
  apiHeaders?: Record<string, string>;
}

interface RawPlayback {
  matchUrn?: string;
  protocol?: string;
  drmEnabled?: boolean;
  manifestUrl?: string;
  drm?: {
    widevine?: { licenseUrl?: string };
    fairplay?: { licenseUrl?: string; certificateUrl?: string };
  };
  serverTime?: string;
  liveStartsAt?: string;
  analytics?: { sid?: string };
}

/**
 * Single GET /v1/playback/{urn}. Throws a typed PlaybackError on non-2xx.
 * @internal Use resolveStream (which adds the live-pickup loop); this is the SDK's internal one-shot.
 */
export async function resolvePlaybackOnce(opts: ResolveOptions): Promise<StreamDescriptor> {
  if (!opts.matchUrn || typeof opts.matchUrn !== 'string') {
    throw new PlaybackError('INVALID_URN', 0, 'resolveStream: a non-empty matchUrn is required');
  }
  const url = joinUrl(opts.baseUrl, `/v1/playback/${encodeURIComponent(opts.matchUrn)}`);
  const res = await fetchWithCredential(url, {
    credential: opts.credential,
    signal: opts.signal,
    apiHeaders: opts.apiHeaders,
  });
  if (!res.ok) throw await toPlaybackError(res);
  const raw = (await res.json()) as RawPlayback;
  return toDescriptor(raw, opts.matchUrn);
}

/**
 * Resolve a match into a playback descriptor (manifest URL + DRM config). With
 * `waitForLive`, blocks (politely, server-paced) until the stream is live.
 */
export function resolveStream(opts: ResolveOptions): Promise<StreamDescriptor> {
  const attempt = () => resolvePlaybackOnce(opts);
  return opts.waitForLive ? pollToLive(attempt, opts.waitForLive, opts.signal) : attempt();
}

async function toPlaybackError(res: Response): Promise<PlaybackError> {
  const retryAfterMs = parseRetryAfterMs(res.headers.get('retry-after'));
  const requestId =
    res.headers.get('x-request-id') ?? res.headers.get('x-amzn-requestid') ?? undefined;
  let message = res.statusText || `HTTP ${res.status}`;
  let serverCode: string | undefined;
  let liveStartsAtMs: number | undefined;
  try {
    const body = (await res.json()) as {
      error?: { code?: string; message?: string };
      liveStartsAt?: string;
    };
    if (body?.error?.message) message = body.error.message;
    if (body?.error?.code) serverCode = body.error.code;
    // On a 425 TOO_EARLY body, newer havik-streams carries `liveStartsAt` so the
    // SDK can widen far-future poll cadence without a separate catalog call.
    if (typeof body?.liveStartsAt === 'string') {
      const t = Date.parse(body.liveStartsAt);
      if (Number.isFinite(t)) liveStartsAtMs = t;
    }
  } catch {
    // non-JSON / empty body — keep the status-text message
  }
  return new PlaybackError(classifyStatus(res.status), res.status, message, {
    retryAfterMs,
    requestId,
    serverCode,
    liveStartsAtMs,
  });
}

const KNOWN_PLAYBACK_KEYS = new Set([
  'matchUrn',
  'protocol',
  'drmEnabled',
  'manifestUrl',
  'drm',
  'serverTime',
  'liveStartsAt',
  'expiresAt',
  'analytics',
]);

// Reject obviously-wrong URL schemes before any of these strings reach a live
// sink (video.src, hls.loadSource, the EME license XHR). The streams API is the
// trust anchor, so this is defense-in-depth against a malformed/compromised
// response — not a substitute for server-side trust. httpStatus 0 marks an
// SDK-side (non-transport) error, matching NETWORK/ABORTED.
function assertHttpUrl(label: string, value: string): string {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    throw new PlaybackError('INTERNAL', 0, `playback ${label} is not a valid URL`);
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    throw new PlaybackError(
      'INTERNAL',
      0,
      `playback ${label} has a disallowed scheme: ${parsed.protocol}`,
    );
  }
  return value;
}

function toDescriptor(raw: RawPlayback, fallbackUrn: string): StreamDescriptor {
  if (!raw.manifestUrl) {
    throw new PlaybackError('INTERNAL', 0, 'playback response is missing manifestUrl');
  }
  const drmEnabled = raw.drmEnabled ?? Boolean(raw.drm);
  const descriptor: StreamDescriptor = {
    matchUrn: raw.matchUrn ?? fallbackUrn,
    protocol: 'hls',
    drmEnabled,
    manifestUrl: assertHttpUrl('manifestUrl', raw.manifestUrl),
    serverTime: raw.serverTime ?? '',
  };
  if (raw.liveStartsAt) descriptor.liveStartsAt = raw.liveStartsAt;
  if (typeof raw.analytics?.sid === 'string' && raw.analytics.sid) {
    descriptor.analytics = { sid: raw.analytics.sid };
  }

  if (drmEnabled && raw.drm) {
    const drm: NonNullable<StreamDescriptor['drm']> = {};
    if (raw.drm.widevine?.licenseUrl) {
      drm.widevine = {
        licenseUrl: assertHttpUrl('widevine licenseUrl', raw.drm.widevine.licenseUrl),
      };
    }
    if (raw.drm.fairplay?.licenseUrl && raw.drm.fairplay.certificateUrl) {
      drm.fairplay = {
        licenseUrl: assertHttpUrl('fairplay licenseUrl', raw.drm.fairplay.licenseUrl),
        certificateUrl: assertHttpUrl('fairplay certificateUrl', raw.drm.fairplay.certificateUrl),
      };
    }
    if (drm.widevine || drm.fairplay) descriptor.drm = drm;
  }

  // Forward-compat: carry any unrecognized top-level fields the server adds (e.g.
  // a future token TTL, CDN hint, or variant) so Mode-B consumers aren't blind to them.
  const extensions: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (!KNOWN_PLAYBACK_KEYS.has(k)) extensions[k] = v;
  }
  if (Object.keys(extensions).length > 0) descriptor.extensions = extensions;

  return descriptor;
}
